import bwipjs from 'bwip-js/browser';

/**
 * Barcodes drawn as vector SVG in millimetres, for artwork (CorelDraw,
 * Illustrator) and for print: the Laetus Pharmacode and the GS1 DataMatrix.
 */

// ---------------------------------------------------------------- Pharmacode

export const PHARMA_MIN = 3;
export const PHARMA_MAX = 131070;

/**
 * The bars of a Pharmacode, left to right: N (thin) and W (thick).  Laetus:
 * while the number is above 0, an even number takes a thick bar and becomes
 * (n - 2) / 2, an odd one a thin bar and (n - 1) / 2; the bars are read
 * right to left.  3 is two thin bars; 131070 sixteen thick ones.
 */
export function pharmacodeBars(n: number): ('N' | 'W')[] {
  if (!Number.isInteger(n) || n < PHARMA_MIN || n > PHARMA_MAX) return [];
  const bars: ('N' | 'W')[] = [];
  let v = n;
  while (v > 0) {
    if (v % 2 === 0) { bars.unshift('W'); v = (v - 2) / 2; } else { bars.unshift('N'); v = (v - 1) / 2; }
  }
  return bars;
}

/** All bars one width (127, 255 ... all thin; 126, 254 ... all thick): it can be read either way round. */
export function uniformPharmacode(n: number): boolean {
  return Number.isInteger(Math.log2(n + 1)) || Number.isInteger(Math.log2(n + 2));
}

/** The number a row of bars reads as - to check a drawing. */
export function pharmacodeValue(bars: ('N' | 'W')[]): number {
  return bars.reduceRight((acc, b, i, arr) => acc + (b === 'N' ? 1 : 2) * 2 ** (arr.length - 1 - i), 0);
}

export type Rotation = 0 | 90 | 180 | 270;
export type TextPlace = 'none' | 'below' | 'above';

export interface PharmaOptions {
  heightMm: number;      // bar height
  narrowMm: number;      // thin bar
  wideMm: number;        // thick bar
  gapMm: number;         // space between bars
  quietMm: number;       // clear space each side
  color: string;         // bars, #rrggbb
  background: string;    // 'none' or #rrggbb
  text: TextPlace;
  fontPt: number;
  rotate: Rotation;
}

/**
 * The house standard, as SimplyPharma2 drew it (e.g. CHARCOL AND CHEDDAR
 * LAVASH_20261056484_672.svg): 0.6 mm thin bar, 1.8 mm thick (3 x thin),
 * 1.2 mm gap (2 x thin), 16 mm high, black, no number, no background.
 */
export const PHARMA_DEFAULTS: PharmaOptions = {
  heightMm: 16, narrowMm: 0.6, wideMm: 1.8, gapMm: 1.2, quietMm: 0,
  color: '#000000', background: 'none', text: 'none', fontPt: 7, rotate: 0,
};

/** File name as SimplyPharma2 gave it: PRODUCT_SAPNO_PHARMACODE (parts left out when blank). */
export function pharmacodeFileName(product: string, sapNo: string, code: string | number): string {
  const clean = (s: string) => s.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return [clean(product), clean(sapNo), String(code)].filter(Boolean).join('_');
}

const r3 = (v: number) => Math.round(v * 1000) / 1000;
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const PT_MM = 25.4 / 72;

/** Rotate a drawing of w x h mm: the SVG's size and the group's transform. */
function rotated(w: number, h: number, rotate: Rotation, body: string, background: string): { svg: string; widthMm: number; heightMm: number } {
  const side = rotate === 90 || rotate === 270;
  const W = r3(side ? h : w), H = r3(side ? w : h);
  const t = rotate === 90 ? `translate(${r3(h)} 0) rotate(90)`
    : rotate === 180 ? `translate(${r3(w)} ${r3(h)}) rotate(180)`
    : rotate === 270 ? `translate(0 ${r3(w)}) rotate(270)` : '';
  const bg = background && background !== 'none' ? `<rect x="0" y="0" width="${W}" height="${H}" fill="${esc(background)}"/>` : '';
  const svg = `<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" width="${W}mm" height="${H}mm" viewBox="0 0 ${W} ${H}">${bg}<g${t ? ` transform="${t}"` : ''}>${body}</g></svg>`;
  return { svg, widthMm: W, heightMm: H };
}

export function pharmacodeSvg(n: number, o: PharmaOptions): { svg: string; widthMm: number; heightMm: number; bars: ('N' | 'W')[] } | null {
  const bars = pharmacodeBars(n);
  if (!bars.length) return null;
  const fontMm = o.fontPt * PT_MM;
  const textBand = o.text === 'none' ? 0 : fontMm * 1.35;
  const barsW = bars.reduce((s, b) => s + (b === 'N' ? o.narrowMm : o.wideMm), 0) + o.gapMm * (bars.length - 1);
  const w = barsW + 2 * o.quietMm;
  const h = o.heightMm + textBand;
  const top = o.text === 'above' ? textBand : 0;
  let x = o.quietMm;
  const rects = bars.map((b) => {
    const bw = b === 'N' ? o.narrowMm : o.wideMm;
    const r = `<rect x="${r3(x)}" y="${r3(top)}" width="${r3(bw)}" height="${r3(o.heightMm)}"/>`;
    x += bw + o.gapMm;
    return r;
  }).join('');
  const text = o.text === 'none' ? ''
    : `<text x="${r3(w / 2)}" y="${r3(o.text === 'above' ? fontMm : o.heightMm + fontMm * 1.15)}" font-family="Arial, Helvetica, sans-serif" font-size="${r3(fontMm)}" text-anchor="middle">${n}</text>`;
  const out = rotated(w, h, o.rotate, `<g fill="${esc(o.color)}">${rects}${text}</g>`, o.background);
  return { ...out, bars };
}

/**
 * The same number's bars by BWIPP's Pharmacode encoder (bwip-js): a second,
 * independent implementation to check ours against.  Its bars are 1 (thin)
 * or 3 (thick) modules wide.
 */
export function referencePharmacodeBars(n: number): ('N' | 'W')[] {
  try {
    const [r] = bwipjs.raw('pharmacode', String(n), {}) as unknown as { sbs: number[] }[];
    return (r?.sbs ?? []).filter((_, i) => i % 2 === 0).map((w) => (w >= 2 ? 'W' : 'N'));
  } catch {
    return [];
  }
}

/**
 * Read a drawn Pharmacode back from its SVG, as a scanner would: the bars
 * left to right, thin or thick by their drawn width, then their value.
 * Uses only the drawing, not the bars it was made from.
 */
export function readPharmacodeSvg(svg: string): { value: number | null; bars: ('N' | 'W')[]; error?: string } {
  // Bar rects carry no fill of their own (the background rect does).
  const rects = [...svg.matchAll(/<rect x="([\d.]+)" y="[\d.]+" width="([\d.]+)" height="[\d.]+"\/>/g)]
    .map((m) => ({ x: Number(m[1]), w: Number(m[2]) }))
    .sort((a, b) => a.x - b.x);
  if (rects.length < 2) return { value: null, bars: [], error: 'No bars found in the drawing' };
  const widths = rects.map((r) => r.w);
  const lo = Math.min(...widths), hi = Math.max(...widths);
  if (hi / lo < 1.5) return { value: null, bars: [], error: 'Every bar is one width, so it cannot be told which way round it reads' };
  const cut = (lo + hi) / 2;
  const bars = rects.map((r) => (r.w < cut ? 'N' : 'W') as 'N' | 'W');
  return { value: pharmacodeValue(bars), bars };
}

/**
 * Check a drawn Pharmacode: it must read back as the number asked for, and
 * its bars must match the independent BWIPP encoder.
 */
export function verifyPharmacode(n: number, svg: string): { ok: boolean; message: string } {
  const read = readPharmacodeSvg(svg);
  const ref = referencePharmacodeBars(n).join('');
  if (read.value == null) return { ok: false, message: read.error ?? 'Could not read the drawing' };
  if (read.value !== n) return { ok: false, message: `The drawing reads as ${read.value}, not ${n}` };
  if (!ref) return { ok: true, message: `Reads back as ${n}` };
  if (read.bars.join('') !== ref) return { ok: false, message: `Reads as ${n}, but the bars differ from the reference encoder (${ref})` };
  return { ok: true, message: `Reads back as ${n} and matches the reference encoder` };
}

// ---------------------------------------------------------------- GS1 DataMatrix

export interface Gs1Fields {
  gtin: string;      // 14 digits (01)
  batch: string;     // (10)
  expiry: string;    // YYYY-MM (17), day 00 = end of month
  mfg: string;       // YYYY-MM (11), optional
  serial: string;    // (21), optional
}

/** GS1 mod-10 check digit of the digits given (without it). */
export function gs1CheckDigit(digits: string): number {
  let sum = 0;
  const d = digits.split('').reverse();
  d.forEach((c, i) => { sum += Number(c) * (i % 2 === 0 ? 3 : 1); });
  return (10 - (sum % 10)) % 10;
}

/** A GTIN as 14 digits: 13 given get their check digit; 8 / 12 are padded. */
export function normaliseGtin(raw: string): { gtin: string; error: string | null } {
  const d = raw.replace(/\D/g, '');
  if (!d) return { gtin: '', error: 'Enter the GTIN' };
  if (d.length === 13) return { gtin: d + gs1CheckDigit(d), error: null };
  if (d.length === 14) {
    return gs1CheckDigit(d.slice(0, 13)) === Number(d[13]) ? { gtin: d, error: null }
      : { gtin: d, error: `Check digit should be ${gs1CheckDigit(d.slice(0, 13))}` };
  }
  if (d.length === 8 || d.length === 12) {
    const p = d.padStart(14, '0');
    return gs1CheckDigit(p.slice(0, 13)) === Number(p[13]) ? { gtin: p, error: null } : { gtin: p, error: 'Check digit does not match' };
  }
  return { gtin: d, error: 'A GTIN has 13 or 14 digits' };
}

const yymm00 = (ym: string) => (/^\d{4}-\d{2}$/.test(ym) ? `${ym.slice(2, 4)}${ym.slice(5, 7)}00` : '');
const GS1_CHARS = /^[!"%&'()*+,\-./0-9:;<=>?A-Z_a-z]*$/;

/** The element string, in the order GS1 recommends: fixed lengths first, serial last. */
export function gs1ElementString(f: Gs1Fields): { text: string; error: string | null } {
  const g = normaliseGtin(f.gtin);
  if (g.error) return { text: '', error: g.error };
  if (!f.batch.trim()) return { text: '', error: 'Enter the batch' };
  if (!f.expiry) return { text: '', error: 'Pick the expiry' };
  for (const [label, v, max] of [['Batch', f.batch.trim(), 20], ['Serial', f.serial.trim(), 20]] as const) {
    if (v.length > max) return { text: '', error: `${label}: up to ${max} characters` };
    if (!GS1_CHARS.test(v)) return { text: '', error: `${label}: letters, digits and - . / only` };
  }
  let s = `(01)${g.gtin}`;
  if (f.mfg) s += `(11)${yymm00(f.mfg)}`;
  s += `(17)${yymm00(f.expiry)}`;
  s += `(10)${f.batch.trim()}`;
  if (f.serial.trim()) s += `(21)${f.serial.trim()}`;
  return { text: s, error: null };
}

/** "MAR.2028" from 2028-03, as cartons print it. */
export function monthText(ym: string): string {
  if (!/^\d{4}-\d{2}$/.test(ym)) return '';
  const m = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'][Number(ym.slice(5, 7)) - 1];
  return `${m}.${ym.slice(0, 4)}`;
}

export interface MatrixOptions {
  moduleMm: number;      // one square
  quietModules: number;  // clear space round it, in squares
  color: string;
  background: string;
  lines: string[];       // text beside it, right
  fontPt: number;
  rotate: Rotation;
}

export const MATRIX_DEFAULTS: MatrixOptions = {
  moduleMm: 0.4, quietModules: 1, color: '#000000', background: 'none', lines: [], fontPt: 6, rotate: 0,
};

/**
 * A GS1 DataMatrix of the element string, square by square, with the text
 * lines (GTIN, batch, dates, serial) set to its right as cartons carry them.
 * Throws the encoder's message when the data is not valid GS1.
 */
export function datamatrixSvg(elementString: string, o: MatrixOptions): { svg: string; widthMm: number; heightMm: number; size: string } {
  const [m] = bwipjs.raw('gs1datamatrix', elementString, {}) as unknown as { pixs: number[]; pixx: number; pixy: number }[];
  if (!m) throw new Error('Could not encode');
  const q = o.quietModules * o.moduleMm;
  const cw = m.pixx * o.moduleMm, ch = m.pixy * o.moduleMm;
  // One rect per run of dark squares on a row: fewer shapes, the same drawing.
  const rects: string[] = [];
  for (let y = 0; y < m.pixy; y++) {
    let x = 0;
    while (x < m.pixx) {
      if (!m.pixs[y * m.pixx + x]) { x++; continue; }
      let run = 1;
      while (x + run < m.pixx && m.pixs[y * m.pixx + x + run]) run++;
      rects.push(`<rect x="${r3(q + x * o.moduleMm)}" y="${r3(q + y * o.moduleMm)}" width="${r3(run * o.moduleMm)}" height="${r3(o.moduleMm)}"/>`);
      x += run;
    }
  }
  const fontMm = o.fontPt * PT_MM;
  const lineH = fontMm * 1.3;
  const textW = o.lines.length ? Math.max(...o.lines.map((l) => l.length)) * fontMm * 0.6 + fontMm : 0;
  const w = cw + 2 * q + textW;
  const h = Math.max(ch + 2 * q, o.lines.length * lineH + 2 * q);
  const textX = cw + 2 * q + fontMm * 0.6;
  const text = o.lines.map((l, i) =>
    `<text x="${r3(textX)}" y="${r3(q + fontMm + i * lineH)}" font-family="Arial, Helvetica, sans-serif" font-size="${r3(fontMm)}">${esc(l)}</text>`).join('');
  const out = rotated(w, h, o.rotate, `<g fill="${esc(o.color)}">${rects.join('')}${text}</g>`, o.background);
  return { ...out, size: `${m.pixx} x ${m.pixy}` };
}

// ---------------------------------------------------------------- colour + files

/** CMYK percentages to #rrggbb (screen approximation - the print colour is the CMYK). */
export function cmykToHex(c: number, m: number, y: number, k: number): string {
  const f = (v: number) => Math.round(255 * (1 - Math.min(100, Math.max(0, v)) / 100) * (1 - Math.min(100, Math.max(0, k)) / 100));
  return `#${[f(c), f(m), f(y)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/** Spot colours carton artwork uses most, as screen colours. */
export const COLOUR_PRESETS: { name: string; hex: string }[] = [
  { name: 'Black', hex: '#000000' },
  { name: 'Pantone 485 C (red)', hex: '#DA291C' },
  { name: 'Pantone 300 C (blue)', hex: '#005EB8' },
  { name: 'Pantone 286 C (blue)', hex: '#0033A0' },
  { name: 'Pantone 354 C (green)', hex: '#00B140' },
  { name: 'Pantone 2603 C (purple)', hex: '#70147A' },
  { name: 'Pantone 021 C (orange)', hex: '#FE5000' },
  { name: 'Reflex Blue', hex: '#001489' },
];

export function downloadText(name: string, text: string, type = 'image/svg+xml') {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/** The SVG as a PNG at the given dots per inch (1200 for print, 300 for screens). */
export async function downloadPng(name: string, svg: string, widthMm: number, heightMm: number, dpi = 1200) {
  const px = (mm: number) => Math.max(1, Math.round((mm / 25.4) * dpi));
  const img = new Image();
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  await new Promise<void>((resolve, reject) => { img.onload = () => resolve(); img.onerror = () => reject(new Error('Could not draw')); img.src = url; });
  const canvas = document.createElement('canvas');
  canvas.width = px(widthMm); canvas.height = px(heightMm);
  canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(url);
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
  if (!blob) throw new Error('Could not make the PNG');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
